// Copyright (c) Docugami, Inc. All rights reserved.

import { IOverflowSetItemProps, IconButton, Modal, OverflowSet, TooltipHost, TooltipOverflowMode } from "@fluentui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { cardShadow } from "components/cards/CardStyles";
import { RichTextSanitizingHtmlViewer } from "components/cards/RichTextSanitizingHtmlViewer";
import { createFolderIdString } from "components/file_management/file_helpers";
import { appContentColor, backgroundGray, primaryTheme } from "components/shell/presentation/AppStyles";
import { useAppSelector } from "hooks/redux";
import { useEffect, useMemo, useRef } from "react";
import { ExampleChunk, selectAllExampleChunks, useGetExampleChunksQuery, useRemoveExampleChunkMutation } from "store/frontend/frontendSlice";
import styled from "styled-components";
import { TokenRangeUtils } from "utils/TokenRangeUtils";
import { SmallButtonLabel, dgTagFontFamily } from "./Fonts";
import { NavigationType, ReportViewMode, useNavigationService } from "hooks/navigationService";
import { selectClusterId, selectPublishedDocumentsView, selectReviewDocumentsView } from "store/reports/selectors";

const ModalTitle = styled.div`
    display: flex;
    justify-content: center;
    box-sizing: border-box;
    font-family: ${dgTagFontFamily};
    font-weight: 700;
    font-size: 18px;
    color: ${appContentColor};
    flex: 1;
`

const ModalHeader = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
`

const DocumentTitle = styled(ModalTitle)`
    border-bottom: 1px solid grey;
    font-weight: 600;
    font-size: 16px;
    padding-bottom: 5px;
    justify-content: left;
    text-overflow: ellipsis;
`

const ModalContent = styled.div`
    padding: 20px 40px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 20px;
`

const ButtonContainer = styled.div`
    display: flex;
    align-items: center;
    background: ${backgroundGray};
    border-radius: 5px;
    padding: 0px 4px;
    margin-left: auto;
    height: 20px;
    justify-content: space-between;
    border-width: 0px;
    cursor: pointer;
    :hover {
        transform: scale(1.1);
    }
`;

const ButtonText = styled(SmallButtonLabel)`
    font-size: 11px;
    margin: 0px 5px;
`;

export interface ExampleChunksModalProps {
    label: string
    onDismiss?: () => void;
    isOpen?: boolean;
    autoDismissWhenEmpty?: boolean;
}

export const ExampleChunksModal = (props: ExampleChunksModalProps) => {
    const clusterId = useAppSelector(selectClusterId);
    const { data } = useGetExampleChunksQuery(!!props.isOpen ? { docsetId: clusterId, label: props.label, includeText: true } : skipToken);
    const documents = useAppSelector(state => state.documents.indexed);
    const dismissRef = useRef(props.onDismiss);

    useEffect(() => {
        dismissRef.current = props.onDismiss;
    }, [props.onDismiss])

    const items = useMemo(() => {
        if (!data?.ids.length) {
            return [];
        }

        const sortedItems = selectAllExampleChunks(data).map(x => ({
            documentName: documents[createFolderIdString(x.docsetId, x.fileId)]?.clusterDocument?.filename,
            item: x
        })).sort((x, y) => {
            if (x.item.fileId === y.item.fileId) {
                return TokenRangeUtils.readingOrderComparer(x.item.tokenRange, y.item.tokenRange);
            }
            else {
                return x.documentName.localeCompare(y.documentName);
            }
        });

        let current: ExampleDocumentGroupProps | undefined;
        const result: Array<ExampleDocumentGroupProps> = [];
        for (const item of sortedItems) {
            if (item.documentName !== current?.documentName) {
                current = {
                    documentName: item.documentName,
                    folderId: createFolderIdString(item.item.docsetId, item.item.fileId),
                    items: []
                }

                result.push(current);
            }

            current.items.push(item.item)
        }

        return result;
    }, [data, documents])

    useEffect(() => {
        if (!!data && !data.ids.length && !!props.autoDismissWhenEmpty) {
            dismissRef.current?.();
        }
    }, [data, props.autoDismissWhenEmpty])

    return (
        <Modal
            styles={{
                root: {
                    overflow: 'hidden',
                },
                main: {
                    overflow: 'hidden',
                    borderRadius: '5px',
                    background: primaryTheme.palette.neutralLighterAlt
                },
                scrollableContent: {
                    maxHeight: '75vh',
                    maxWidth: '50vw',
                    minWidth: '400px'
                }
            }}
            isOpen={props.isOpen}
            onDismiss={props.onDismiss}
            isBlocking={false}
            isDarkOverlay={true}>
            <ModalContent>
                <ModalHeader>
                    <ModalTitle>Examples - {props.label}</ModalTitle>
                    <ButtonContainer onClick={props.onDismiss}>
                        <ButtonText>Close</ButtonText>
                    </ButtonContainer>
                </ModalHeader>
                {
                    items.map(x => <ExampleDocumentGroup key={x.documentName} {...x} />)
                }
            </ModalContent>
        </Modal>
    );
}

const ExampleItemDocumentContainer = styled.div`
    border-radius: 5px;
    background: ${primaryTheme.palette.white};
    box-shadow: ${cardShadow};
    padding: 15px;
    gap: 12px;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    position: relative;
`

const ExampleItemContainer = styled(ExampleItemDocumentContainer)`
`

const ExampleItemContent = styled.div`
    max-height: 400px;
    overflow: auto;
    box-sizing: border-box;
`

const OverflowContainer = styled.div`
    position: absolute;
    top: 0px;
    right: 0px;
    visibility: hidden;
    ${ExampleItemContainer}:hover & {
        visibility: visible;
    }
`

interface ExampleDocumentGroupProps {
    documentName: string;
    folderId: string;
    items: ExampleChunk[];
}

const ExampleDocumentGroup = (props: ExampleDocumentGroupProps) => {
    const reviewDocumentsView = useAppSelector(selectReviewDocumentsView);
    const publishedDocumentsView = useAppSelector(selectPublishedDocumentsView);

    const canNavigate = !!reviewDocumentsView?.entities[props.folderId] || !!publishedDocumentsView?.entities[props.folderId];
    const isReadonly = !!publishedDocumentsView?.entities[props.folderId] || !reviewDocumentsView?.entities[props.folderId];

    const navService = useNavigationService();
    const navigate = (item: ExampleChunk) => {
        navService.navToReportPage(
            NavigationType.ReplaceCurrentPage,
            {
                mode: ReportViewMode.Document,
                documentId: props.folderId,
                instanceId: item.spanId
            });
    }

    return (
        <ExampleItemDocumentContainer>
            <TooltipHost content={props.documentName} overflowMode={TooltipOverflowMode.Self}>
                <DocumentTitle>{props.documentName}</DocumentTitle>
            </TooltipHost>
            {
                props.items.map(x => <ExampleChunkItem key={x.id} item={x} onNavigate={canNavigate ? () => navigate(x) : undefined} readonly={isReadonly} />)
            }
        </ExampleItemDocumentContainer>
    )
}

interface ExampleChunkItemProps {
    item: ExampleChunk;
    readonly?: boolean;
    onNavigate?: () => void;
}

const ExampleChunkItem = (props: ExampleChunkItemProps) => {
    const [triggerRemove, removeStatus] = useRemoveExampleChunkMutation();
    function onRenderOverflowButton(overflowItems: any[] | undefined): JSX.Element {
        return (
            <IconButton
                iconProps={{ iconName: "More" }}
                styles={{
                    root: {
                        height: "15px",
                        borderRadius: '0px 5px 0px 0px'
                    },
                    icon: {
                        fontSize: '15px',
                        margin: 0,
                        color: appContentColor
                    },
                    rootHovered: { backgroundColor: primaryTheme.palette.neutralLighterAlt }
                }}
                menuProps={{ items: overflowItems!, styles: { root: { backgroundColor: primaryTheme.palette.white } } }}
                onRenderMenuIcon={() => null} />
        )
    }

    const overflowItems = useMemo(() => {
        let overflowItems: IOverflowSetItemProps[] | undefined;
        if (!props.readonly) {
            overflowItems = overflowItems || [];
            overflowItems.push({
                key: "remove",
                name: "Remove example",
                disabled: removeStatus.isLoading,
                onClick: () => triggerRemove(props.item)
            })
        }

        if (!!props.onNavigate) {
            overflowItems = overflowItems || [];
            overflowItems.push({
                key: "navigate",
                name: "View in document",
                disabled: false,
                onClick: props.onNavigate
            })
        }

        return overflowItems;
    }, [triggerRemove, props.item, props.onNavigate, removeStatus.isLoading, props.readonly])

    return (
        <ExampleItemContainer>
            <OverflowContainer>
                <OverflowSet overflowItems={overflowItems} onRenderItem={() => null} onRenderOverflowButton={onRenderOverflowButton} />
            </OverflowContainer>
            <ExampleItemContent>
                <RichTextSanitizingHtmlViewer html={props.item.richText} />
            </ExampleItemContent>
        </ExampleItemContainer>
    )
}
